// Template214: Osaka - Extra Katsuo-ji (Page11)
// Intro, El templo de la victoria, Miles de Darumas, Cómo llegar, tip final.

import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

const Template214 = ({
	backgroundColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page?.texts?.[0]?.content;
	const intro = page?.texts?.[1]?.content;
	const temploTitle = page?.texts?.[2]?.content;
	const temploBody = page?.texts?.[3]?.content;
	const darumasTitle = page?.texts?.[4]?.content;
	const darumasBody = page?.texts?.[5]?.content;
	const llegarTitle = page?.texts?.[6]?.content;
	const llegarList = page?.texts?.[7]?.content;
	const tipEntrada = page?.texts?.[8]?.content;
	const tipFinal = page?.texts?.[9]?.content;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found?.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found?.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const darumaImage = getImageSrc("daruma");
	const templeImage = getImageSrc("temple");
	const logoImage = getCanvasImageUrl("logo");

	const sabiasQue = Array.isArray(page?.data?.sabiasQue)
		? page.data.sabiasQue
		: [];

	const actualHeaderColor = headerBackgroundColor || "#1F4E3D";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: "#444",
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{intro && (
				<Box data-block-id="text-1" sx={{ padding: "10px 12px" }}>
					<Box sx={{ fontSize: 13, color: "#333", margin: 0 }}>{intro}</Box>
				</Box>
			)}

			<Box
				data-block-id="text-2"
				sx={{
					padding: "10px 12px",
					paddingTop: 0,
					display: "flex",
					flexDirection: "column",
					gap: 1.75,
				}}
			>
				{/* El templo */}
				<Box data-block-id="text-3" sx={{ display: "flex", gap: 2, position: "relative" }}>
					{darumaImage && (
						<Box
							component="img"
							src={darumaImage}
							alt=""
							sx={{
								width: "40%",
								borderRadius: "12px",
								objectFit: "cover",
								height: 110,
							}}
						/>
					)}
					<Box sx={{ width: "60%" }}>
						{temploTitle && (
							<Box component="h3" sx={estiloH3}>
								{temploTitle}
							</Box>
						)}
						{temploBody && (
							<Box component="p" sx={estiloP}>
								{temploBody}
							</Box>
						)}
					</Box>
					{sabiasQue.length > 0 && (
						<>
							<Box sx={{ position: "absolute", top: 0, left: "33%" }}>
								<SabiasQue
									icono={sabiasQue[0]?.icono ?? "🎯"}
									texto={sabiasQue[0]?.texto ?? ""}
								/>
							</Box>
							{sabiasQue[1] && (
								<Box sx={{ position: "absolute", top: 40, left: "33%" }}>
									<SabiasQue
										icono={sabiasQue[1]?.icono ?? "🎎"}
										texto={sabiasQue[1]?.texto ?? ""}
									/>
								</Box>
							)}
						</>
					)}
				</Box>

				{(darumasTitle || darumasBody) && (
					<Box
						sx={{
							backgroundColor: "#d1e6de",
							padding: 1.5,
							borderRadius: "12px",
							display: "flex",
							gap: 2,
							alignItems: "center",
							position: "relative",
						}}
					>
						<Box sx={{ width: "100%" }}>
							{darumasTitle && (
								<Box component="h3" sx={{ ...estiloH3, color: "#1F4E3D" }}>
									{darumasTitle}
								</Box>
							)}
							{darumasBody && (
								<Box component="p" sx={estiloP}>
									{darumasBody}
								</Box>
							)}
						</Box>
					</Box>
				)}

				{/* Cómo llegar */}
				<Box data-block-id="text-5" sx={{ position: "relative" }}>
					{(llegarTitle || llegarList) && (
						<Box
							sx={{
								backgroundColor: "#d1e6de",
								border: "3px solid #1F4E3D",
								borderRadius: "12px",
								padding: "16px 20px",
								fontSize: 12,
								color: "#333",
								width: isSmallScreen ? "70%" : "100%",
							}}
						>
							{llegarTitle && (
								<Box
									component="h4"
									sx={{
										margin: 0,
										marginBottom: 1,
										fontSize: 13,
										fontWeight: 700,
										color: "#1F4E3D",
										textTransform: "uppercase",
									}}
								>
									{llegarTitle}
								</Box>
							)}
							{llegarList && (
								<Box
									component="ul"
									sx={{ paddingLeft: 2.25, margin: 0 }}
									dangerouslySetInnerHTML={{ __html: llegarList }}
								/>
							)}
						</Box>
					)}
					{templeImage && (
						<Box
							component="img"
							src={templeImage}
							alt=""
							sx={{ width: 95, position: "absolute", bottom: -10, right: 0 }}
						/>
					)}
					{tipEntrada && (
						<Box
							sx={{
								position: "absolute",
								bottom: 50,
								right: isSmallScreen ? 0 : 20,
								backgroundColor: "#fff8db",
								border: "2px solid #f5c400",
								borderRadius: "16px 16px 0px 16px",
								padding: "10px 12px",
								fontSize: 11,
								color: "#444",
								boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
								maxWidth: 150,
							}}
							dangerouslySetInnerHTML={{ __html: tipEntrada }}
						/>
					)}
				</Box>

				{tipFinal && (
					<Box
						data-block-id="text-6"
						sx={{
							backgroundColor: "#d1e6de",
							border: "2px solid #1F4E3D",
							borderRadius: "16px",
							padding: "12px 16px",
							fontSize: 12,
							color: "#444",
						}}
					>
						<Box
							component="p"
							sx={estiloP}
							dangerouslySetInnerHTML={{ __html: tipFinal }}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template214.thumbnail = "/images/template-1.png";
export default Template214;
